import { Slider, type SliderProps } from '@mui/material';
import { cn } from 'cn';
import { type ReactNode, useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type SliderPosition = number | null;

type P = {
  /** Id of an element describing the slider, e.g. a hint rendered outside the field. */
  readonly describedBy?: string;
  readonly hint?: string;
  readonly isDisabled?: boolean;
  readonly label?: string;
  /**
   * Discrete non-linear allowed values. When provided, the slider snaps to
   * exactly these values and the form field stores the actual value (not an index).
   * Note: this is distinct from MUI's `step` prop (uniform step size).
   */
  readonly customSteps?: readonly number[];
  /**
   * Optional formatter for the tick mark labels below the slider track,
   * the value bubble, and `aria-valuetext`. Defaults to rendering the raw number.
   */
  readonly formatMarkLabel?: (value: number) => ReactNode;
  /**
   * When set, prepends a leftmost slider position that stores `null` and
   * renders this label on the tick, value bubble, and `aria-valuetext`.
   */
  readonly unspecifiedLabel?: string;
} & Omit<
  SliderProps,
  | 'value'
  | 'onChange'
  | 'onBlur'
  | 'disabled'
  | 'name'
  | 'valueLabelFormat'
  | 'getAriaValueText'
  | 'slotProps'
>;

function positionLabel(
  position: SliderPosition | undefined,
  unspecifiedLabel: string | undefined,
  formatMarkLabel: ((value: number) => ReactNode) | undefined,
): ReactNode {
  if (position == null) {
    return unspecifiedLabel ?? '';
  }

  return formatMarkLabel ? formatMarkLabel(position) : position;
}

function fieldErrorMessage(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return String(error);
}

function labelAsString(label: ReactNode): string {
  if (typeof label === 'string' || typeof label === 'number') {
    return String(label);
  }

  return '';
}

export function SliderField({
  className,
  customSteps,
  describedBy,
  formatMarkLabel,
  hint,
  isDisabled = false,
  label,
  unspecifiedLabel,
  ...rest
}: P) {
  const field = useFieldContext<number | null>();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const hasErrors = field.state.meta.errors.length > 0;

  const id = useId();

  const sortedCustomSteps = customSteps
    ? customSteps.toSorted((a, b) => a - b)
    : undefined;
  const positions: readonly SliderPosition[] | undefined = sortedCustomSteps
    ? unspecifiedLabel === undefined
      ? sortedCustomSteps
      : [null, ...sortedCustomSteps]
    : undefined;

  const customStepsProps: Partial<SliderProps> = positions
    ? {
        marks: positions.map((position, i) => ({
          label: positionLabel(position, unspecifiedLabel, formatMarkLabel),
          value: i,
        })),
        max: positions.length - 1,
        min: 0,
        step: 1,
      }
    : {};

  const positionLabelProps: Partial<SliderProps> = positions
    ? {
        getAriaValueText: (index) =>
          labelAsString(
            positionLabel(positions[index], unspecifiedLabel, formatMarkLabel),
          ),
        valueLabelFormat: (index) =>
          positionLabel(positions[index], unspecifiedLabel, formatMarkLabel),
      }
    : {};

  const sliderValue = positions
    ? Math.max(
        0,
        positions.findIndex((position) =>
          Object.is(position, field.state.value),
        ),
      )
    : (field.state.value ?? 0);

  return (
    <div className={cn('relative', className)}>
      {Boolean(label) && (
        <InputLabel
          hint={hint}
          htmlFor={id}
          isRequired={isRequired}
          className="mb-1"
        >
          {label}
        </InputLabel>
      )}
      <div>
        <Slider
          slotProps={{ input: { 'aria-describedby': describedBy, id } }}
          disabled={isDisabled}
          value={sliderValue}
          onBlur={field.handleBlur}
          onChange={(_event, newValue) => {
            field.handleChange(
              positions
                ? (positions[newValue as number] ?? null)
                : (newValue as number),
            );
          }}
          {...customStepsProps}
          {...rest}
          {...positionLabelProps}
        />
      </div>
      {hasErrors ? (
        <ValidationError>
          {unique(field.state.meta.errors.map(fieldErrorMessage)).join(', ')}
        </ValidationError>
      ) : null}
    </div>
  );
}
